import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import TextAlign from '@tiptap/extension-text-align';
import { Box, Paper, ToggleButtonGroup, ToggleButton, Divider, Tooltip } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatClear,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Code,
  DataObject,
  Undo,
  Redo,
} from '@mui/icons-material';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  error?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Введите текст...',
  minHeight = 200,
  error,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder,
      }),
      Typography,
      // Alignment is stored as an inline `text-align` style on the block node;
      // the backend sanitiser keeps exactly that style on these tags.
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: value,
    // Without this the toolbar never re-renders on a selection change, so the
    // active state of every button stayed frozen at whatever it was on mount.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
  });

  // The form fills `value` asynchronously (reset() after the dialog mounts, or a
  // record loaded later), so the editor has to follow an external change.
  // `emitUpdate: false` keeps this from bouncing back through onChange.
  useEffect(() => {
    if (!editor) return;
    const next = value || '';
    if (next !== editor.getHTML()) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const marks = [
    editor.isActive('bold') && 'bold',
    editor.isActive('italic') && 'italic',
    editor.isActive('underline') && 'underline',
    editor.isActive('strike') && 'strike',
  ].filter(Boolean) as string[];

  const alignment =
    (['left', 'center', 'right', 'justify'] as const).find(a =>
      editor.isActive({ textAlign: a }),
    ) ?? 'left';

  const blocks = [
    editor.isActive('bulletList') && 'bulletList',
    editor.isActive('orderedList') && 'orderedList',
    editor.isActive('blockquote') && 'blockquote',
    editor.isActive('code') && 'code',
    editor.isActive('codeBlock') && 'codeBlock',
  ].filter(Boolean) as string[];

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: error ? 'error.main' : 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          flexWrap: 'wrap',
        }}
      >
        {/* Inline marks — each button drives its own command, so toggling one
            never re-toggles the others (the old group onChange did exactly that). */}
        <ToggleButtonGroup size="small" value={marks} aria-label="text formatting">
          <ToggleButton
            value="bold"
            aria-label="bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Tooltip title="Жирный">
              <FormatBold fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="italic"
            aria-label="italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Tooltip title="Курсив">
              <FormatItalic fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="underline"
            aria-label="underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <Tooltip title="Подчёркнутый">
              <FormatUnderlined fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="strike"
            aria-label="strikethrough"
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <Tooltip title="Зачёркнутый">
              <FormatStrikethrough fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={alignment}
          aria-label="text alignment"
        >
          <ToggleButton
            value="left"
            aria-label="align left"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <Tooltip title="По левому краю">
              <FormatAlignLeft fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="center"
            aria-label="align center"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <Tooltip title="По центру">
              <FormatAlignCenter fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="right"
            aria-label="align right"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <Tooltip title="По правому краю">
              <FormatAlignRight fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="justify"
            aria-label="align justify"
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          >
            <Tooltip title="По ширине">
              <FormatAlignJustify fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        <ToggleButtonGroup size="small" value={blocks} aria-label="block formatting">
          <ToggleButton
            value="bulletList"
            aria-label="bullet list"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <Tooltip title="Маркированный список">
              <FormatListBulleted fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="orderedList"
            aria-label="ordered list"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <Tooltip title="Нумерованный список">
              <FormatListNumbered fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="blockquote"
            aria-label="blockquote"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <Tooltip title="Цитата">
              <FormatQuote fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="code"
            aria-label="inline code"
            onClick={() => editor.chain().focus().toggleCode().run()}
          >
            <Tooltip title="Код в строке">
              <Code fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="codeBlock"
            aria-label="code block"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <Tooltip title="Блок кода">
              <DataObject fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        <ToggleButtonGroup size="small" aria-label="clear formatting">
          <ToggleButton
            value="clear"
            aria-label="clear formatting"
            // Marks first, then block types, then alignment: otherwise a
            // centred heading keeps its style after "clearNodes".
            onClick={() =>
              editor
                .chain()
                .focus()
                .unsetAllMarks()
                .clearNodes()
                .unsetTextAlign()
                .run()
            }
          >
            <Tooltip title="Убрать форматирование">
              <FormatClear fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        <ToggleButtonGroup size="small" aria-label="history">
          <ToggleButton
            value="undo"
            aria-label="undo"
            onClick={() => editor.chain().focus().undo().run()}
          >
            <Tooltip title="Отменить">
              <Undo fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="redo"
            aria-label="redo"
            onClick={() => editor.chain().focus().redo().run()}
          >
            <Tooltip title="Повторить">
              <Redo fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          '& .tiptap-editor': {
            minHeight: `${minHeight}px`,
            padding: '12px',
            outline: 'none',
            '&:focus': {
              outline: 'none',
            },
            '& p': {
              margin: '0 0 8px 0',
              '&:last-child': {
                marginBottom: 0,
              },
            },
            '& ul, & ol': {
              margin: '0 0 8px 0',
              paddingLeft: '24px',
            },
            '& blockquote': {
              margin: '0 0 8px 0',
              paddingLeft: '12px',
              borderLeft: '3px solid #ccc',
            },
            '& code': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              padding: '2px 4px',
              borderRadius: '4px',
              fontFamily: 'monospace',
            },
            '& pre': {
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              padding: '10px 12px',
              borderRadius: '4px',
              margin: '0 0 8px 0',
              overflowX: 'auto',
              '& code': {
                backgroundColor: 'transparent',
                padding: 0,
              },
            },
            '& p.is-editor-empty:first-child::before': {
              color: 'adb5bd',
              content: 'attr(data-placeholder)',
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
          },
        }}
      >
        <EditorContent editor={editor} className="tiptap-editor" />
      </Box>
    </Paper>
  );
}
